// B2B Approvals queue — pending customers + pending credit applications
import { useState, useCallback, useMemo } from "react";
import {
  CheckCircle,
  UserCheck,
  Landmark,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import {
  useB2BCustomers,
  useSuspendB2BCustomer,
  useCreditApplications,
  useApproveCredit,
  useRejectCredit,
} from "@/hooks/admin/useB2B";
import type { B2BCustomer, CreditApplication } from "@/lib/admin-api/b2b";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type Tab = "customers" | "credit";

const HIGH_VALUE_THRESHOLD_FCFA = 5_000_000;

// ---------------------------------------------------------------------------
// Reject modal
// ---------------------------------------------------------------------------

interface RejectModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly applicationId: string | null;
  readonly companyName: string;
  readonly onReject: (id: string, notes: string) => Promise<void>;
  readonly isRejecting: boolean;
}

function RejectNoteModal({
  open,
  onClose,
  applicationId,
  companyName,
  onReject,
  isRejecting,
}: RejectModalProps) {
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    setNotes("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!applicationId) return;
    if (!notes.trim()) {
      setError("Une note est requise.");
      return;
    }
    setError(null);
    try {
      await onReject(applicationId, notes);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur.");
    }
  }, [applicationId, notes, onReject, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title="Rejeter la demande"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isRejecting}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isRejecting}
            className="gap-1.5 bg-red-600 hover:bg-red-700"
          >
            Rejeter
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-stone-600">
          Motif du rejet pour{" "}
          <span className="font-semibold">{companyName}</span>
        </p>
        <textarea
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Motif du rejet..."
          className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
        />
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Approval chain indicator
// ---------------------------------------------------------------------------

function ApprovalChainBadge({
  amount,
}: {
  readonly amount: number;
}) {
  const isHighValue = amount >= HIGH_VALUE_THRESHOLD_FCFA;
  return (
    <div className="flex items-center gap-1.5">
      {isHighValue ? (
        <span className="flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
          <AlertTriangle className="h-3 w-3" />
          Directeur requis
        </span>
      ) : (
        <span className="flex items-center gap-1 rounded-md border border-stone-200 bg-stone-50 px-2 py-0.5 text-xs text-stone-600">
          <CheckCircle className="h-3 w-3" />
          Manager
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Approvals() {
  const [activeTab, setActiveTab] = useState<Tab>("customers");
  const [rejectingCreditId, setRejectingCreditId] = useState<string | null>(
    null,
  );
  const [rejectingCompany, setRejectingCompany] = useState("");

  const { data: customerData, isLoading: customersLoading } = useB2BCustomers({
    status: "pending_approval",
  });
  const { data: creditData, isLoading: creditLoading } = useCreditApplications(
    {
      status: "pending",
    },
  );
  const activateCustomer = useSuspendB2BCustomer();
  const approveCredit = useApproveCredit();
  const rejectCredit = useRejectCredit();

  const pendingCustomers = customerData?.customers ?? [];
  const pendingCredits = creditData?.applications ?? [];

  // KPI data
  const kpiData = useMemo(() => {
    const totalPending = pendingCustomers.length + pendingCredits.length;
    const highValueCredits = pendingCredits.filter(
      (c) => c.requested_amount_fcfa >= HIGH_VALUE_THRESHOLD_FCFA,
    ).length;
    const totalCreditAmount = pendingCredits.reduce(
      (s, c) => s + c.requested_amount_fcfa,
      0,
    );
    return {
      totalPending,
      pendingCustomers: pendingCustomers.length,
      pendingCredits: pendingCredits.length,
      highValueCredits,
      totalCreditAmount,
    };
  }, [pendingCustomers, pendingCredits]);

  const handleApproveCustomer = useCallback(
    (customer: B2BCustomer) => {
      activateCustomer.mutate({ id: customer.id, activate: true });
    },
    [activateCustomer],
  );

  const handleApproveCredit = useCallback(
    (app: CreditApplication) => {
      approveCredit.mutate(app.id);
    },
    [approveCredit],
  );

  const handleRejectCredit = useCallback(
    async (id: string, notes: string) => {
      await rejectCredit.mutateAsync({ id, notes });
      setRejectingCreditId(null);
    },
    [rejectCredit],
  );

  const customerColumns: readonly ColumnDef<B2BCustomer>[] = useMemo(
    () => [
      {
        id: "company_name",
        header: "Entreprise",
        cell: (c) => (
          <div>
            <p className="font-medium text-stone-900">{c.company_name}</p>
            <p className="text-xs text-stone-500">{c.contact_name}</p>
          </div>
        ),
      },
      {
        id: "email",
        header: "Email",
        cell: (c) => (
          <span className="text-sm text-stone-600">{c.email}</span>
        ),
      },
      {
        id: "phone",
        header: "Telephone",
        cell: (c) => (
          <span className="text-sm text-stone-600">{c.phone}</span>
        ),
      },
      {
        id: "region",
        header: "Region",
        cell: (c) => (
          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs text-stone-700">
            {c.region}
          </span>
        ),
      },
      {
        id: "credit_limit",
        header: "Limite credit demandee",
        cell: (c) => (
          <span className="text-sm font-medium text-stone-900">
            {c.credit_limit_fcfa.toLocaleString("fr-SN")} FCFA
          </span>
        ),
      },
      {
        id: "created_at",
        header: "Soumis le",
        cell: (c) => (
          <span className="text-sm text-stone-500">
            {new Date(c.created_at).toLocaleDateString("fr-SN")}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (c) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleApproveCustomer(c)}
              className="flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Approuver
            </button>
          </div>
        ),
      },
    ],
    [handleApproveCustomer],
  );

  const creditColumns: readonly ColumnDef<CreditApplication>[] = useMemo(
    () => [
      {
        id: "company_name",
        header: "Entreprise",
        cell: (a) => (
          <span className="font-medium text-stone-900">{a.company_name}</span>
        ),
      },
      {
        id: "requested_amount_fcfa",
        header: "Montant demande",
        cell: (a) => (
          <div>
            <span className="text-sm font-medium text-stone-900">
              {a.requested_amount_fcfa.toLocaleString("fr-SN")} FCFA
            </span>
            {a.requested_amount_fcfa >= HIGH_VALUE_THRESHOLD_FCFA && (
              <p className="mt-0.5 text-xs text-amber-600">Montant eleve</p>
            )}
          </div>
        ),
      },
      {
        id: "approval_chain",
        header: "Niveau d'approbation",
        cell: (a) => <ApprovalChainBadge amount={a.requested_amount_fcfa} />,
      },
      {
        id: "submitted_at",
        header: "Soumis le",
        cell: (a) => (
          <span className="text-sm text-stone-500">
            {new Date(a.submitted_at).toLocaleDateString("fr-SN")}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        cell: (a) => (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleApproveCredit(a)}
              className="flex items-center gap-1 rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Approuver
            </button>
            <button
              type="button"
              onClick={() => {
                setRejectingCreditId(a.id);
                setRejectingCompany(a.company_name);
              }}
              className="flex items-center gap-1 rounded-md bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-100"
            >
              <XCircle className="h-3.5 w-3.5" />
              Rejeter
            </button>
          </div>
        ),
      },
    ],
    [handleApproveCredit],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
            <CheckCircle className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-xl font-semibold text-stone-900">
              File d'approbations
            </h1>
            <p className="text-sm text-stone-500">
              {kpiData.totalPending} element(s) en attente de traitement
            </p>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Total en attente"
            value={kpiData.totalPending}
            format="number"
            icon={<Clock className="h-4 w-4" />}
          />
          <KPICard
            label="Clients en attente"
            value={kpiData.pendingCustomers}
            format="number"
            icon={<UserCheck className="h-4 w-4" />}
          />
          <KPICard
            label="Credits en attente"
            value={kpiData.pendingCredits}
            format="number"
            icon={<Landmark className="h-4 w-4" />}
          />
          <KPICard
            label="Montant credit total"
            value={kpiData.totalCreditAmount}
            format="currency"
          />
        </div>

        {/* High-value alert */}
        {kpiData.highValueCredits > 0 && (
          <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              <strong>{kpiData.highValueCredits}</strong> demande(s) de credit
              depassent{" "}
              {HIGH_VALUE_THRESHOLD_FCFA.toLocaleString("fr-SN")} FCFA et
              necessitent l'approbation du directeur.
            </span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 rounded-lg border border-stone-200 bg-stone-50 p-1 w-fit">
          <button
            type="button"
            onClick={() => setActiveTab("customers")}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "customers"
                ? "bg-white text-stone-900 shadow-sm"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            <UserCheck className="h-4 w-4" />
            Clients en attente
            {pendingCustomers.length > 0 && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                {pendingCustomers.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("credit")}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === "credit"
                ? "bg-white text-stone-900 shadow-sm"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            <Landmark className="h-4 w-4" />
            Credits en attente
            {pendingCredits.length > 0 && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs text-amber-700">
                {pendingCredits.length}
              </span>
            )}
          </button>
        </div>

        {/* Customers tab */}
        {activeTab === "customers" && (
          <>
            {!customersLoading && pendingCustomers.length === 0 ? (
              <EmptyState
                icon={<UserCheck className="h-6 w-6" />}
                title="Aucun client en attente"
                description="Tous les clients ont été traités."
              />
            ) : (
              <AdminDataTable
                data={pendingCustomers}
                columns={customerColumns}
                isLoading={customersLoading}
              />
            )}
          </>
        )}

        {/* Credit tab */}
        {activeTab === "credit" && (
          <>
            {!creditLoading && pendingCredits.length === 0 ? (
              <EmptyState
                icon={<Landmark className="h-6 w-6" />}
                title="Aucune demande de credit en attente"
                description="Toutes les demandes ont été traitées."
              />
            ) : (
              <AdminDataTable
                data={pendingCredits}
                columns={creditColumns}
                isLoading={creditLoading}
              />
            )}
          </>
        )}
      </div>

      <RejectNoteModal
        open={rejectingCreditId !== null}
        onClose={() => setRejectingCreditId(null)}
        applicationId={rejectingCreditId}
        companyName={rejectingCompany}
        onReject={handleRejectCredit}
        isRejecting={rejectCredit.isPending}
      />
    </AdminLayout>
  );
}
